/* ============================================================
   ICON COLLECTION STRIP — JavaScript
   Handles carousel scrolling, dot navigation, arrows, and auto-scroll
   ============================================================ */

class IconCollectionStrip {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.track = section.querySelector('[data-track]');
    this.trackClip = section.querySelector('[data-track-clip]');
    this.prevBtn = section.querySelector('[data-prev]');
    this.nextBtn = section.querySelector('[data-next]');
    this.progressContainer = section.querySelector('[data-progress]');
    this.dots = section.querySelectorAll('[data-dot]');
    this.items = section.querySelectorAll('[data-item-index]');

    // Auto-scroll settings
    this.autoScrollEnabled = section.dataset.autoScroll === 'true';
    this.autoScrollSpeed = parseInt(section.dataset.autoScrollSpeed, 10) * 1000 || 4000;
    this.autoScrollInterval = null;

    // Responsive dot settings
    this.dotsDesktop = parseInt(this.progressContainer?.dataset.dotsDesktop || 3, 10);
    this.dotsMobile = parseInt(this.progressContainer?.dataset.dotsMobile || 4, 10);
    this.visibleDots = this.dotsDesktop; // Default to desktop

    // State
    this.currentPosition = 0;
    this.itemCount = this.items.length;

    if (this.itemCount === 0) return;

    this.init();
  }

  init() {
    // Update visible dots based on screen size
    this.updateResponsiveDots();

    // Bind event listeners
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.handlePrev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.handleNext());

    this.dots.forEach((dot) => {
      dot.addEventListener('click', (e) => this.handleDotClick(e));
    });

    // Stop auto-scroll on manual interaction
    this.track.addEventListener('scroll', () => this.resetAutoScroll(), { passive: true });
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.resetAutoScroll());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.resetAutoScroll());

    // Start auto-scroll if enabled
    if (this.autoScrollEnabled) {
      this.startAutoScroll();
    }

    // Initial button state
    this.updateButtonStates();
    this.updateDots();

    // Listen for scroll to update UI
    this.track.addEventListener('scroll', () => this.onTrackScroll(), { passive: true });

    // Listen for window resize to update dot visibility
    window.addEventListener('resize', () => this.updateResponsiveDots());

    // Add mouse wheel scroll functionality
    this.track.addEventListener('wheel', (e) => this.handleMouseWheel(e), { passive: false });

    // Cleanup on section unload
    this.section.addEventListener('shopify:section:unload', () => this.destroy());
  }

  handleMouseWheel(e) {
    // Handle mouse wheel scroll - convert vertical scroll to horizontal
    if (e.deltaY !== 0) {
      e.preventDefault();
      
      // Better scroll sensitivity for smoother scrolling
      const scrollAmount = e.deltaY > 0 ? 150 : -150;
      
      this.track.scrollLeft += scrollAmount;
      
      // Reset auto-scroll timer on manual wheel scroll
      if (this.autoScrollEnabled) {
        this.resetAutoScroll();
      }
    }
  }

  updateResponsiveDots() {
    // Determine visible dots based on screen width
    const isMobile = window.innerWidth < 750;
    this.visibleDots = isMobile ? this.dotsMobile : this.dotsDesktop;
    
    // Cap visibleDots to actual number of dots created
    this.visibleDots = Math.min(this.visibleDots, this.dots.length);

    // Show/hide dots based on visible count
    this.dots.forEach((dot, index) => {
      if (index < this.visibleDots) {
        dot.style.display = 'block';
      } else {
        dot.style.display = 'none';
      }
    });

    this.updateDots();
  }

  handlePrev() {
    const itemWidth = this.getItemWidth();
    const itemsPerView = Math.round(this.trackClip.clientWidth / itemWidth);
    this.track.scrollBy({
      left: -(itemWidth * itemsPerView),
      behavior: 'smooth',
    });
  }

  handleNext() {
    const itemWidth = this.getItemWidth();
    const itemsPerView = Math.round(this.trackClip.clientWidth / itemWidth);
    this.track.scrollBy({
      left: itemWidth * itemsPerView,
      behavior: 'smooth',
    });
  }

  handleDotClick(e) {
    const dotIndex = parseInt(e.target.dataset.dot, 10);
    const itemWidth = this.getItemWidth();
    const itemsPerView = Math.round(this.trackClip.clientWidth / itemWidth);
    const scrollAmount = itemWidth * itemsPerView * dotIndex;

    this.track.scrollTo({
      left: scrollAmount,
      behavior: 'smooth',
    });
  }

  getItemWidth() {
    if (this.items.length === 0) return 0;
    const firstItem = this.items[0];
    return firstItem.offsetWidth + parseFloat(window.getComputedStyle(this.track).gap || 0);
  }

  onTrackScroll() {
    this.updateButtonStates();
    this.updateDots();
  }

  updateButtonStates() {
    if (!this.prevBtn || !this.nextBtn) return;

    const scrollLeft = this.track.scrollLeft;
    const scrollWidth = this.track.scrollWidth;
    const clientWidth = this.track.clientWidth;

    // Disable prev button if at start
    this.prevBtn.disabled = scrollLeft < 10;

    // Disable next button if at end
    this.nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 10;
  }

  updateDots() {
    if (this.dots.length === 0 || this.visibleDots === 0) return;

    const itemWidth = this.getItemWidth();
    const scrollLeft = this.track.scrollLeft;
    const itemsPerView = Math.round(this.trackClip.clientWidth / itemWidth);
    const slideAmount = itemWidth * itemsPerView;
    
    // Calculate which slide we're currently on (0-based)
    const activeSlide = Math.round(scrollLeft / slideAmount);
    
    // Only activate dots that are visible
    this.dots.forEach((dot, index) => {
      if (dot.style.display === 'none') return;
      
      if (index === activeSlide) {
        dot.classList.add('is-active');
        dot.setAttribute('aria-selected', 'true');
      } else {
        dot.classList.remove('is-active');
        dot.setAttribute('aria-selected', 'false');
      }
    });
  }

  startAutoScroll() {
    this.autoScrollInterval = setInterval(() => {
      const itemWidth = this.getItemWidth();
      const itemsPerView = Math.round(this.trackClip.clientWidth / itemWidth);
      const currentScroll = this.track.scrollLeft;
      const slideAmount = itemWidth * itemsPerView;
      const totalSlides = Math.ceil(this.itemCount / itemsPerView);
      
      // Get current slide index (0-based)
      const currentSlideIndex = Math.round(currentScroll / slideAmount);
      
      // If at last slide, go back to start. Otherwise, go to next slide
      if (currentSlideIndex >= totalSlides - 1) {
        this.track.scrollTo({
          left: 0,
          behavior: 'smooth',
        });
      } else {
        this.track.scrollBy({
          left: slideAmount,
          behavior: 'smooth',
        });
      }
    }, this.autoScrollSpeed);
  }

  resetAutoScroll() {
    if (this.autoScrollEnabled) {
      clearInterval(this.autoScrollInterval);
      this.startAutoScroll();
    }
  }

  destroy() {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-section-id]').forEach((section) => {
    new IconCollectionStrip(section);
  });
});

// Shopify theme editor support
if (window.Shopify && window.Shopify.theme) {
  document.addEventListener('shopify:section:load', (e) => {
    new IconCollectionStrip(e.detail.section);
  });
}